/**
 * Mehrfachauswahl der Datenquellen für die Zeitachse.
 *
 * „google" steht für die Google-Zeitachse (Aufenthalte, Wege, Rohspur); jede
 * weitere Kennung ist eine Positionsquelle (BMW, iPhone, Find My, Freunde,
 * AirTags). Die Auswahl lebt in der Adresszeile, nicht im Komponentenzustand —
 * so ist eine Ansicht teilbar und der Zurück-Knopf tut das Erwartete.
 */

import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown, Layers } from "lucide-react";

import type { QuelleInfo } from "@/api.ts";

export const GOOGLE = "google";

export function QuellenAuswahl({
  quellen,
  gewaehlt,
  beiWechsel,
}: {
  quellen: QuelleInfo[];
  gewaehlt: Set<string>;
  beiWechsel: (neu: Set<string>) => void;
}) {
  const [offen, setOffen] = useState(false);
  const rahmen = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!offen) return;
    const zu = (e: MouseEvent) => {
      if (rahmen.current && !rahmen.current.contains(e.target as Node)) setOffen(false);
    };
    document.addEventListener("mousedown", zu);
    return () => document.removeEventListener("mousedown", zu);
  }, [offen]);

  const umschalten = (id: string) => {
    const neu = new Set(gewaehlt);
    if (neu.has(id)) neu.delete(id);
    else neu.add(id);
    beiWechsel(neu);
  };

  // Nur Quellen mit Daten anbieten — leere stiften nur Verwirrung.
  const mitDaten = quellen.filter((q) => q.anzahl > 0);
  const anzahl = gewaehlt.size;

  return (
    <div ref={rahmen} className="relative">
      <button
        onClick={() => setOffen((o) => !o)}
        className={`flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm hover:bg-page-bg ${
          anzahl > 1 || !gewaehlt.has(GOOGLE) ? "border-primary text-primary" : "border-border"
        }`}
        title="Quellen wählen"
      >
        <Layers size={15} />
        <span className="hidden sm:inline">Quellen</span>
        {anzahl > 1 && (
          <span className="rounded-full bg-primary/10 px-1.5 text-xs">{anzahl}</span>
        )}
        <ChevronDown size={14} className={offen ? "rotate-180 transition-transform" : "transition-transform"} />
      </button>

      {offen && (
        <div className="absolute left-0 top-full z-20 mt-1 max-h-80 w-64 overflow-y-auto rounded-lg border border-border bg-surface py-1 shadow-lg">
          <Zeile
            farbe="#3b82f6"
            name="Google-Zeitachse"
            aktiv={gewaehlt.has(GOOGLE)}
            beiKlick={() => umschalten(GOOGLE)}
          />
          {mitDaten.length > 0 && <div className="my-1 border-t border-border" />}
          {mitDaten.map((q) => (
            <Zeile
              key={q.id}
              farbe={q.farbe}
              name={q.name}
              zusatz={`${q.anzahl.toLocaleString("de-DE")}`}
              aktiv={gewaehlt.has(q.id)}
              beiKlick={() => umschalten(q.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function Zeile({
  farbe,
  name,
  zusatz,
  aktiv,
  beiKlick,
}: {
  farbe: string;
  name: string;
  zusatz?: string;
  aktiv: boolean;
  beiKlick: () => void;
}) {
  return (
    <button
      onClick={beiKlick}
      className="flex w-full items-center gap-2.5 px-3 py-1.5 text-left text-sm hover:bg-page-bg"
    >
      <span
        className="flex size-4 shrink-0 items-center justify-center rounded"
        style={{ backgroundColor: aktiv ? farbe : "transparent", border: `1.5px solid ${farbe}` }}
      >
        {aktiv && <Check size={11} className="text-white" strokeWidth={3} />}
      </span>
      <span className="min-w-0 flex-1 truncate">{name}</span>
      {zusatz && <span className="shrink-0 text-xs text-text-muted">{zusatz}</span>}
    </button>
  );
}
